"use client";

import Link from "next/link";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { githubDark } from "@uiw/codemirror-theme-github";
import TrySeozastCTA from "@/app/components/TrySeozastCTA";
import Head from "next/head";

const Page = () => {
  const canonicalUrl = 'https://seozast.com/articles/how-to-render-pages-dynamically-in-nextjs-for-seo';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Head con canonical */}
      <Head>
        <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-4">How to Render Pages Dynamically in Next.js for SEO</h1>
        <p className="text-md mb-4">
          Dynamic page rendering in Next.js is a game-changer for creating SEO-friendly web applications. By generating pages automatically from a data source like a JSON file, you can scale content effortlessly, improve crawlability, and boost search engine rankings. This 1000-word guide explains how to render pages dynamically using a JSON-based index, compares it to Markdown-based rendering, and shows how tools like SEOZast can automate the process. For related techniques, explore our guides on <Link href="/articles/how-to-create-a-dynamic-sitemap-in-nextjs" className="text-emerald-500 hover:underline">dynamic sitemaps</Link>, <Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">Markdown rendering</Link>, <Link href="/articles/what-is-pseo-and-seo" className="text-emerald-500 hover:underline">PSEO and SEO basics</Link>, <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation</Link>, and <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Why Dynamic Page Rendering?</h2>
        <p className="text-md mb-4">
          Dynamic page rendering allows you to generate web pages programmatically from a data source, such as a JSON file or database, without hardcoding routes. This approach is ideal for content-heavy sites like blogs, documentation, or e-commerce platforms. It enhances SEO by enabling dynamic metadata (e.g., titles, descriptions), automatic page generation, and integration with sitemaps for better crawlability. Unlike static rendering, dynamic rendering adapts to new content seamlessly, making it perfect for Programmatic SEO (PSEO), as discussed in our <Link href="/articles/what-is-pseo-and-seo" className="text-emerald-500 hover:underline">PSEO and SEO basics guide</Link>. Tools like SEOZast can further automate content creation, as shown in our <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Project Folder Structure</h2>
        <p className="text-md mb-4">
          Here’s the folder structure for a Next.js project using JSON-based dynamic page rendering, optimized for SEO:
        </p>
        <ul className="list-disc pl-6 text-md mb-4">
          <li><code>app/articles/[slug]/page.tsx</code>: Dynamic route for individual article pages.</li>
          <li><code>app/articles/page.tsx</code>: Index page listing all articles from <code>articles.json</code>.</li>
          <li><code>public/articles.json</code>: Generated JSON file containing article metadata (slug, title, lastmod).</li>
          <li><code>scripts/generate-articles-json.mjs</code>: Script to scan articles and generate <code>articles.json</code>.</li>
          <li><code>app/sitemap.xml/route.ts</code>: Dynamic sitemap for crawlability, as shown in our <Link href="/articles/how-to-create-a-dynamic-sitemap-in-nextjs" className="text-emerald-500 hover:underline">dynamic sitemap guide</Link>.</li>
          <li><code>app/components/TrySeozastCTA.tsx</code>: CTA component for SEOZast registration.</li>
          <li><code>styles/</code>: Tailwind CSS for consistent styling.</li>
        </ul>
        <p className="text-md mb-4">
          The <code>articles.json</code> file acts as a centralized index, making it easy to manage article metadata. The <code>generate-articles-json.mjs</code> script automates its creation, while <code>app/articles/page.tsx</code> renders a user-friendly article list. This structure supports scalability and integrates with SEOZast for automated content, as detailed in our <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Dynamic Page Rendering Code</h2>
        <p className="text-md mb-4">
          Below is the code to render pages dynamically in Next.js using a JSON-based index. It includes the article index page, a script to generate <code>articles.json</code>, and a <code>package.json</code> configuration.
        </p>

        <h3 className="text-xl font-semibold mb-2">Article Index Page (<code>app/articles/page.tsx</code>)</h3>
        <CodeMirror
          value={`import { notFound } from "next/navigation";
import Link from "next/link";
import path from "path";
import fs from "fs";

interface Article {
  slug: string;
  title: string;
  lastmod: string;
}

export default async function ArticlesPage() {
  let articles: Article[] = [];
  
  try {
    const filePath = path.join(process.cwd(), "public/articles.json");
    
    if (!fs.existsSync(filePath)) {
      console.error("articles.json not found. Make sure to run the generation script first.");
      return notFound();
    }
    
    const data = fs.readFileSync(filePath, "utf8");
    articles = JSON.parse(data);
    
    // Sort articles by last modified date (newest first)
    articles.sort((a, b) => new Date(b.lastmod).getTime() - new Date(a.lastmod).getTime());
  } catch (error) {
    console.error("Error loading articles:", error);
    return notFound();
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6">Articles</h1>
      
      {articles.length === 0 ? (
        <p className="text-gray-500">No articles available.</p>
      ) : (
        <ul className="space-y-4">
          {articles.map(({ slug, title, lastmod }) => (
            <li key={slug} className="border-b pb-4">
              <Link 
                href={\`/articles/\${slug}\`}
                className="text-xl font-medium text-emerald-500 hover:text-emerald-600 hover:underline"
              >
                {title}
              </Link>
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {new Date(lastmod).toLocaleDateString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="600px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This page reads <code>articles.json</code> and displays a sorted list of articles with titles, links, and last modified dates, ensuring an SEO-friendly index. It uses error handling to manage missing or invalid JSON files.
        </p>

        <h3 className="text-xl font-semibold mb-2">Article JSON Generation Script (<code>scripts/generate-articles-json.mjs</code>)</h3>
        <CodeMirror
          value={`import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/**
 * @typedef {Object} ArticlePage
 * @property {string} slug
 * @property {Date} lastmod
 * @property {string} title
 */

function scanDirectory(dir, parentPath = "", articles = []) {
  const items = fs.readdirSync(dir);

  for (const item of items) {
    if (item.startsWith("[") || item === "api") continue;

    const itemPath = path.join(dir, item);
    const stat = fs.statSync(itemPath);

    if (stat.isDirectory()) {
      const pageFile = path.join(itemPath, "page.tsx");

      if (fs.existsSync(pageFile)) {
        const slug = parentPath ? \`\${parentPath}/\${item}\` : item;
        
        // Extraer un título del nombre de la carpeta (puedes mejorar esto)
        const title = item
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        articles.push({
          slug,
          title,
          lastmod: stat.mtime.toISOString(),
        });
      }

      scanDirectory(itemPath, parentPath ? \`\${parentPath}/\${item}\` : item, articles);
    }
  }

  return articles;
}

function generateArticlesJson() {
  try {
    const rootDir = path.resolve(__dirname, '..');
    const articlesDir = path.join(rootDir, "app", "articles");
    
    if (!fs.existsSync(articlesDir)) {
      console.log(\`⚠️ Directory doesn't exist: \${articlesDir}\`);
      fs.mkdirSync(articlesDir, { recursive: true });
      console.log(\`✅ Created directory: \${articlesDir}\`);
    }
    
    const articles = scanDirectory(articlesDir);

    const outputPath = path.join(rootDir, "public", "articles.json");
    
    // Asegúrate de que el directorio public existe
    if (!fs.existsSync(path.join(rootDir, "public"))) {
      fs.mkdirSync(path.join(rootDir, "public"), { recursive: true });
    }
    
    fs.writeFileSync(outputPath, JSON.stringify(articles, null, 2));
    console.log(\`✅ Generated \${articles.length} articles to public/articles.json\`);
  } catch (err) {
    console.error("❌ Failed to generate articles.json", err);
    process.exit(1);
  }
}

generateArticlesJson();`}
          extensions={[javascript()]}
          theme={githubDark}
          height="600px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This script scans the <code>app/articles</code> directory, generates article metadata (slug, title, lastmod), and writes it to <code>public/articles.json</code>. It runs automatically after builds via the <code>package.json</code> script.
        </p>

        <h3 className="text-xl font-semibold mb-2">Package.json Configuration</h3>
        <CodeMirror
          value={`{
  "scripts": {
    "postbuild": "node scripts/generate-articles-json.mjs"
  }
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="200px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This <code>postbuild</code> script ensures <code>articles.json</code> is regenerated after each build, keeping the article index up-to-date for SEO.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">JSON vs. Markdown Rendering</h2>
        <p className="text-md mb-4">
          The JSON-based approach differs from Markdown rendering (as shown in our <Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">Markdown rendering guide</Link>) in several ways:
        </p>
        <ul className="list-disc pl-6 text-md mb-4">
          <li><strong>JSON Rendering</strong>: Uses a centralized <code>articles.json</code> file for metadata, ideal for listing pages without full content processing. It’s lightweight and fast for index pages but requires separate page files for content.</li>
          <li><strong>Markdown Rendering</strong>: Processes full Markdown files with frontmatter, perfect for content-heavy pages with rich formatting, TOCs, and metadata. It’s more flexible for articles but heavier for simple listings.</li>
          <li><strong>Use Case</strong>: Use JSON for scalable index pages and sitemap integration; use Markdown for detailed article pages with rich content.</li>
        </ul>
        <p className="text-md mb-4">
          Combining both approaches—JSON for indexing and Markdown for content—maximizes SEO efficiency. SEOZast can automate both, generating JSON metadata and Markdown files, as discussed in our <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">SEO Benefits of Dynamic Rendering</h2>
        <p className="text-md mb-4">
          Dynamic page rendering offers several SEO advantages:
        </p>
        <ul className="list-disc pl-6 text-md mb-4">
          <li><strong>Scalability</strong>: Automatically generates pages for new content, ideal for PSEO.</li>
          <li><strong>Crawlability</strong>: Integrates with dynamic sitemaps to ensure search engines index all pages.</li>
          <li><strong>Metadata</strong>: Supports dynamic titles and descriptions for better click-through rates.</li>
          <li><strong>Automation</strong>: Tools like SEOZast streamline content creation and deployment, reducing manual effort.</li>
        </ul>
        <p className="text-md mb-4">
          Learn more about these benefits in our <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Conclusion</h2>
        <p className="text-md mb-4">
          Dynamic page rendering in Next.js, using a JSON-based index, is a powerful way to build SEO-friendly web apps. By automating page generation with <code>articles.json</code> and scripts, you can scale content efficiently and ensure search engines crawl your site effectively. Pair this approach with a dynamic sitemap (<Link href="/articles/how-to-create-a-dynamic-sitemap-in-nextjs" className="text-emerald-500 hover:underline">dynamic sitemap guide</Link>) and Markdown rendering for content-heavy pages (<Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">Markdown rendering guide</Link>). Tools like SEOZast make it even easier by automating content creation and deployment for just $5/month, as shown in our <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link>. Start optimizing your app’s SEO today!
        </p>
        <TrySeozastCTA />
      </section>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is dynamic page rendering in Next.js?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dynamic page rendering in Next.js refers to generating pages automatically from a data source like JSON or Markdown, enabling scalable content and improved SEO."
                }
              },
              {
                "@type": "Question",
                "name": "How do I render pages dynamically using JSON in Next.js?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can use a JSON file containing article metadata and programmatically generate routes using Next.js dynamic routing and file system functions like fs and path."
                }
              },
              {
                "@type": "Question",
                "name": "What is the benefit of using a JSON index for SEO?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Using a JSON index allows you to centralize metadata for articles, making it easier to create dynamic sitemaps and keep content up-to-date for search engines."
                }
              },
              {
                "@type": "Question",
                "name": "Should I use JSON or Markdown for dynamic rendering?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "JSON is best for lightweight metadata and index pages, while Markdown is ideal for rich content. Combining both offers maximum flexibility and SEO performance."
                }
              }
            ]
          })
        }}
      />
    </div>
  );
};

export default Page;